import React, { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Button, ButtonProps, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

type CopyState = "idle" | "success" | "error"

export interface ClipboardButtonProps
  extends Omit<ButtonProps, "onClick" | "icon"> {
  defaultIcon?: IconName
  successIcon?: IconName
  errorIcon?: IconName
  resetDelay?: number
  onClick?: () => boolean | void | Promise<boolean | void>
}

export const ClipboardButton = React.memo(
  React.forwardRef<HTMLButtonElement, ClipboardButtonProps>(
    (
      {
        defaultIcon = IconName.DUPLICATE,
        successIcon = IconName.CHECK,
        errorIcon = IconName.CLOSE,
        resetDelay = 2000,
        onClick,
        className,
        label,
        ...rest
      },
      ref
    ) => {
      const { t } = useTranslation()
      const [copyState, setCopyState] = useState<CopyState>("idle")
      const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

      const scheduleReset = useCallback(() => {
        if (resetTimerRef.current !== null) {
          clearTimeout(resetTimerRef.current)
        }
        resetTimerRef.current = setTimeout(() => {
          setCopyState("idle")
          resetTimerRef.current = null
        }, resetDelay)
      }, [resetDelay])

      const handleClick = useCallback(async () => {
        if (!onClick) return
        const result = await onClick()
        const nextState: CopyState = result === false ? "error" : "success"
        setCopyState(nextState)
        scheduleReset()
      }, [onClick, scheduleReset])

      const icon = useMemo<IconName>(() => {
        const icons: Record<CopyState, IconName> = {
          idle: defaultIcon,
          success: successIcon,
          error: errorIcon
        }
        return icons[copyState]
      }, [copyState, defaultIcon, successIcon, errorIcon])

      return (
        <Button
          variant={ButtonVariant.SECONDARY}
          isIconOnly
          {...rest}
          className={classNames(className, "btn--bleed", "btn--borderless")}
          ref={ref}
          icon={icon}
          iconSize={IconSize.SMALL}
          // Icon-only buttons have no text node, so Button's label -> aria-label
          // forwarding is the only accessible name available.
          label={label || t("General.Copy")}
          onClick={handleClick}
        />
      )
    }
  )
)
